import dotProp from 'dot-prop';
import { FieldAttributes } from 'formik';
import { Variants, VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { State } from '../store/cases/types';
import { AvailableInputs, EditedField, Field, FIELDS, INPUTS } from './types';
import { Option } from 'form/components/Select/Select';
import { Option as GlobalOption } from 'shared/interface/globalInterface';
import {
  AdviserFcaInput,
  BankAccNumberInput,
  CaseNumberInput,
  Checkbox,
  CurrencyInput,
  DateInput,
  DecimalCurrencyInput,
  DecimalNumericInput,
  FormInputWrapper,
  NumericInput,
  PercentageDecimalInput,
  PercentageInput,
  PhoneInput,
  PostcodeInput,
  Select,
  SortCodeInput,
  TextArea,
  TextInput,
  YearsInput,
} from '../../../form/infrastructure/reactHookForm';
import {
  AdviserFcaInput as CleanAdviserFcaInput,
  BankAccNumberInput as CleanBankAccNumberInput,
  CaseNumberInput as CleanCaseNumberInput,
  CurrencyInput as CleanCurrencyInput,
  DateInput as CleanDateInput,
  DecimalNumericInput as CleanDecimalNumericInput,
  InputWrapper as CleanInputWrapper,
  NumericInput as CleanNumericInput,
  PercentageDecimalInput as CleanPercentageDecimalInput,
  PercentageInput as CleanPercentageInput,
  PhoneInput as CleanPhoneInput,
  PostcodeInput as CleanPostcodeInput,
  SortCodeInput as CleanSortCodeInput,
  TextInput as CleanTextInput,
  YearsInput as CleanYearsInput,
} from '../../../form/components';
import { useFormContext } from 'react-hook-form';
import { Icon } from 'lmc-ui-library';
import styled from 'styled-components';

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export type Props = {
  modulePrefix: string;
  field: Field;
  fieldType: FIELDS;
  variant?: Variants;
  onFieldChange: (editedField: EditedField) => void;
} & FieldAttributes<any>;

type FieldProps = {
  field: Field;
  onTextFieldBlur?: () => void;
  onCheckboxClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onSelectOption?: (option: GlobalOption) => void;
  props?: any;
  value?: any;
};

const getFieldPath = (field: Field) => {
  return field.prefix ? `${field.prefix}${field.name}` : field.name;
};

const getFullPath = (modulePrefix: string, field: Field) =>
  getFieldPath({ ...field, prefix: `${modulePrefix}${field.prefix || ''}` });

const textInputVariantMap = new Map([
  [VARIANTS_TYPES.CURRENCY, ({ props }: any) => <CurrencyInput {...props} />],
  [VARIANTS_TYPES.DECIMAL_CURRENCY, ({ props }: any) => <DecimalCurrencyInput {...props} />],
  [VARIANTS_TYPES.DATE, ({ props }: any) => <DateInput placeholder={'DD/MM/YYYY'} {...props} />],
  [VARIANTS_TYPES.POSTCODE, ({ props }: any) => <PostcodeInput {...props} />],
  [VARIANTS_TYPES.PHONE, ({ props }: any) => <PhoneInput {...props} />],
  [VARIANTS_TYPES.PERCENTAGE, ({ props }: any) => <PercentageInput {...props} />],
  [VARIANTS_TYPES.DECIMAL_PERCENTAGE, ({ props }: any) => <PercentageDecimalInput {...props} />],
  [VARIANTS_TYPES.NUMBER, ({ props }) => <NumericInput {...props} />],
  [VARIANTS_TYPES.ADVISER_FCA, ({ props }) => <AdviserFcaInput {...props} />],
  [VARIANTS_TYPES.SORT_CODE, ({ props }) => <SortCodeInput {...props} />],
  [VARIANTS_TYPES.BANK_ACC_NUMBER, ({ props }) => <BankAccNumberInput {...props} />],
  [VARIANTS_TYPES.CASE_NUMBER, ({ props }) => <CaseNumberInput {...props} />],
  [VARIANTS_TYPES.YEARS, ({ props }) => <YearsInput {...props} />],
  [VARIANTS_TYPES.DECIMAL_NUMBER, ({ props }) => <DecimalNumericInput {...props} />],
]);

const cleanTextInputVariantMap = new Map([
  [VARIANTS_TYPES.CURRENCY, ({ props }: any) => <CleanCurrencyInput {...props} />],
  [VARIANTS_TYPES.DECIMAL_CURRENCY, ({ props }: any) => <CleanCurrencyInput {...props} />],
  [VARIANTS_TYPES.DATE, ({ props }: any) => <CleanDateInput {...props} />],
  [VARIANTS_TYPES.POSTCODE, ({ props }: any) => <CleanPostcodeInput {...props} />],
  [VARIANTS_TYPES.PHONE, ({ props }: any) => <CleanPhoneInput {...props} />],
  [VARIANTS_TYPES.PERCENTAGE, ({ props }: any) => <CleanPercentageInput {...props} />],
  [VARIANTS_TYPES.DECIMAL_PERCENTAGE, ({ props }: any) => <CleanPercentageDecimalInput {...props} />],
  [VARIANTS_TYPES.NUMBER, ({ props }: any) => <CleanNumericInput {...props} />],
  [VARIANTS_TYPES.ADVISER_FCA, ({ props }: any) => <CleanAdviserFcaInput {...props} />],
  [VARIANTS_TYPES.SORT_CODE, ({ props }: any) => <CleanSortCodeInput {...props} />],
  [VARIANTS_TYPES.BANK_ACC_NUMBER, ({ props }: any) => <CleanBankAccNumberInput {...props} />],
  [VARIANTS_TYPES.CASE_NUMBER, ({ props }: any) => <CleanCaseNumberInput {...props} />],
  [VARIANTS_TYPES.YEARS, ({ props }: any) => <CleanYearsInput {...props} />],
  [VARIANTS_TYPES.DECIMAL_NUMBER, ({ props }: any) => <CleanDecimalNumericInput {...props} />],
]);

const getSelectOptions = (options: GlobalOption[], withDefaultOption?: boolean) => {
  const defaultOption: Option = {
    selectedRender: <span>Please select</span>,
    label: 'Please select',
    render: <span>Please select</span>,
    name: '',
    selected: false,
  };

  return withDefaultOption
    ? [
        defaultOption,
        ...options.map((option: GlobalOption) => ({
          label: option.option,
          name: option.value.toString(),
          selected: false,
        })),
      ]
    : [
        ...options.map((option: GlobalOption) => ({
          label: option.option,
          name: option.value.toString(),
          selected: false,
        })),
      ];
};

const fieldsMap: Map<AvailableInputs, React.FC<FieldProps>> = new Map<AvailableInputs, React.FC<FieldProps>>([
  [
    INPUTS.TEXT,
    ({ field, onTextFieldBlur, props }: FieldProps) => {
      const variantGetter = textInputVariantMap.get(props.variant);
      return (
        <FormInputWrapper
          title={field.label}
          name={getFieldPath(field)}
          extraContent={props.extraContent ? props.extraContent : undefined}
          style={{ margin: '12px 0' }}>
          {variantGetter ? variantGetter({ field, onTextFieldBlur, props }) : <TextInput {...props} />}
        </FormInputWrapper>
      );
    },
  ],
  [
    INPUTS.SELECT,
    ({ field, props: { searchable = false, withDefaultOption = false, isNested = false, ...props } }: FieldProps) => {
      const { setValue } = useFormContext();
      const initialOptions = getSelectOptions(props.options, withDefaultOption);
      const [initialData, setInitialData] = useState<Option[]>(initialOptions);
      const [optionsList, setOptionsList] = useState<Option[]>(initialOptions);

      const reloadSearchOptions = (key: string) => {
        const filteredOptions = initialData.filter(
          (option: Option) =>
            option.name.toLowerCase().includes(key.toLowerCase()) ||
            option.label.toLowerCase().includes(key.toLowerCase()),
        );

        setOptionsList(filteredOptions);
      };

      useEffect(() => {
        const reloadedValues = getSelectOptions(props.options, withDefaultOption);

        setInitialData(reloadedValues);
        setOptionsList(reloadedValues);
      }, [props.options, withDefaultOption]);

      return (
        <FormInputWrapper
          title={field.label}
          name={getFieldPath(field)}
          extraContent={props.extraContent ? props.extraContent : undefined}
          isNested={isNested}
          style={{ margin: '12px 0' }}>
          <Select
            options={optionsList}
            nullable={true}
            onOptionClick={(option) => {
              setValue(getFieldPath(field), option.name, { shouldValidate: true, shouldDirty: true });
            }}
            searchable={searchable}
            style={{ fontWeight: 'normal' }}
            onSearchInputChange={(val) => reloadSearchOptions(val)}
            rightIcon={(stateColor, setFocused, isFocused) => (
              <IconWrapper onClick={() => (isFocused ? setFocused(false) : setFocused(true))}>
                <Icon type={isFocused ? 'iconUp' : 'iconDown'} color={'#2d3540'} />
              </IconWrapper>
            )}
            disabled={props.disabled ? props.disabled : false}
            autoHeight={true}
          />
        </FormInputWrapper>
      );
    },
  ],
  [
    INPUTS.CHECKBOX,
    ({ field, props }: FieldProps) => (
      <FormInputWrapper
        title={''}
        name={getFieldPath(field)}
        extraContent={props.extraContent ? props.extraContent : undefined}
        style={{ margin: '12px 0' }}>
        <Checkbox title={field.label} disabled={props.disabled ? props.disabled : false} />
      </FormInputWrapper>
    ),
  ],
  [
    INPUTS.TEXTAREA,
    ({ field, onTextFieldBlur, props }: FieldProps) => (
      <FormInputWrapper
        title={field.label}
        name={getFieldPath(field)}
        extraContent={props.extraContent ? props.extraContent : undefined}
        style={{ margin: '12px 0' }}>
        <TextArea title={field.label} rows={3} onBlur={onTextFieldBlur} {...props} />
      </FormInputWrapper>
    ),
  ],
]);

export const EditableField: React.FC<Props> = ({ modulePrefix = '', fieldType, field, onFieldChange, ...props }) => {
  const updateFieldProcessProvider = useCallback(
    (field: Field) => {
      const update = (value: any) => {
        onFieldChange({ field, value, fullPath: getFullPath(modulePrefix, field) });
      };

      return typeof onFieldChange === 'function' ? update : () => {};
    },
    [onFieldChange, modulePrefix],
  );

  const updateFieldProcess = useMemo(() => updateFieldProcessProvider(field), [updateFieldProcessProvider, field]);

  const handleTextFieldBlur = () => {
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent<HTMLInputElement>) => {
    updateFieldProcess(e.currentTarget.checked);
  };

  const handleSelectOption = (option: GlobalOption) => {
    updateFieldProcess(option.value);
  };

  const ComponentGetter = fieldsMap.get(fieldType);
  return (
    <>
      {ComponentGetter
        ? ComponentGetter({
            field,
            onTextFieldBlur: handleTextFieldBlur,
            onCheckboxClick: handleCheckboxClick,
            onSelectOption: handleSelectOption,
            props,
          })
        : ''}
    </>
  );
};

export const ReadonlyField = ({ field, onTextFieldBlur, props, value }: FieldProps) => {
  const variantGetter = cleanTextInputVariantMap.get(props.variant);
  return (
    <CleanInputWrapper title={field.label} name={getFieldPath(field)}>
      {variantGetter ? (
        variantGetter({ field, onTextFieldBlur, props: { ...props, value, disabled: true } })
      ) : (
        <CleanTextInput disabled={true} defaultValue={value} />
      )}
    </CleanInputWrapper>
  );
};

interface StoreReadonlyFieldProps {
  field: Field;
  prefix?: string;
  variant?: Variants;
  props?: any;
}

export const StoreReadonlyField = ({ prefix = '', field, ...props }: StoreReadonlyFieldProps) => {
  const value = useSelector(({ currentCase }: { currentCase: State }) => {
    return dotProp.get(currentCase.readonlyFields, `${prefix}${getFieldPath(field)}`);
  });

  return <ReadonlyField field={field} props={props} value={value} />;
};
